import type { ListCache } from '@heraion/shared';

// web-rnw twin of ./asyncListCache.ts (see that file's header) — same
// namespace + JSON shape, localStorage instead of AsyncStorage. Resolved
// instead of the bare file by Vite's `.web.ts` extension preference.

type Options = {
  version?: number;
};

const namespace = (cacheKey: string, version: number) =>
  `jl.asyncList.${cacheKey}.v${version}`;

export function asyncStorageListCache<T>(
  cacheKey: string,
  opts: Options = {},
): ListCache<T> {
  const storageKey = namespace(cacheKey, opts.version ?? 1);
  return {
    read: async () => {
      try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as { items?: unknown } | null;
        if (!parsed || !Array.isArray(parsed.items)) return null;
        return parsed.items as T[];
      } catch {
        return null; // private mode / corrupt entry — treat as empty
      }
    },
    write: async (items) => {
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({ cachedAt: new Date().toISOString(), items }),
        );
      } catch {
        // quota exceeded / private mode — non-fatal, network still drives.
      }
    },
  };
}

export type ResourceCache<T> = {
  read: () => Promise<T | null>;
  write: (value: T) => Promise<void>;
};

/** The resource cache plus the age of what it holds. `cachedAtMs` null: a
 *  blob written without a date. */
export type AgedResourceCache<T> = ResourceCache<T> & {
  readEntry: () => Promise<{ value: T; cachedAtMs: number | null } | null>;
  /** Drop the blob (#336: a program editor draft that was saved or
   *  discarded). Best-effort like `write`. */
  clear: () => Promise<void>;
};

export function asyncStorageResourceCache<T>(
  cacheKey: string,
  opts: Options = {},
): AgedResourceCache<T> {
  const storageKey = namespace(cacheKey, opts.version ?? 1);
  const readEntry = async () => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { value?: unknown; cachedAt?: unknown } | null;
      if (!parsed || parsed.value === undefined) return null;
      const at = typeof parsed.cachedAt === 'string' ? Date.parse(parsed.cachedAt) : NaN;
      return { value: parsed.value as T, cachedAtMs: Number.isFinite(at) ? at : null };
    } catch {
      return null;
    }
  };
  return {
    readEntry,
    clear: async () => {
      try {
        await localStorage.removeItem(storageKey);
      } catch {
        // Best-effort, as write.
      }
    },
    read: async () => (await readEntry())?.value ?? null,
    write: async (value) => {
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({ cachedAt: new Date().toISOString(), value }),
        );
      } catch {
        // quota exceeded / private mode — non-fatal.
      }
    },
  };
}
